import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { simulationService } from '~/services';
import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import { useMutationInvalidator } from '~/hooks/useMutationInvalidator';
import type { UnifiedSimulationUpload } from '~/components/simulation/shared/uploadAdapter';

// Upload templates are always served by simulation-service, regardless of the sim's engine version.
const UPLOAD_TEMPLATE_ENDPOINT = '/simulation-proxy/api/v1/simulation/uploads/upload-template';

export function templateFileName(includeAllFields: boolean): string {
  return includeAllFields ? 'SIMULATION_FULL_UPLOAD_TEMPLATE.csv' : 'SIMULATION_MINIMAL_UPLOAD_TEMPLATE.csv';
}

export function triggerBrowserDownload(href: string, download?: string): void {
  const link = document.createElement('a');
  link.setAttribute('href', href);
  if (download) {
    link.setAttribute('download', download);
  }
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// simulation-service streams raw CSV bytes; there is no { downloadUrl } wrapper.
async function downloadTemplate(includeAllFields: boolean): Promise<void> {
  const res = await GatewayAxiosClient.get<Blob>(UPLOAD_TEMPLATE_ENDPOINT, {
    params: { includeAllFields },
    responseType: 'blob',
  });
  const href = URL.createObjectURL(res.data);
  triggerBrowserDownload(href, templateFileName(includeAllFields));
  setTimeout(() => URL.revokeObjectURL(href), 0);
}

export function useSimulationUploadTemplateDownload(): (includeAllFields: boolean) => Promise<void> {
  return useCallback((includeAllFields: boolean) => downloadTemplate(includeAllFields), []);
}

// Validate is v1-only: v2 uploads validate asynchronously on the Uploads page, so there's no
// inline v2 flow here. Keyed solely on the S3 key.
interface ValidateParams {
  s3Key: string;
}
interface ValidateCallbacks {
  onSuccess: (result: UnifiedSimulationUpload) => void;
  onError: (err: unknown) => void;
}

export function useValidateSimulationUpload(): {
  validate: (params: ValidateParams, cb: ValidateCallbacks) => void;
  isLoading: boolean;
} {
  const validateLegacy = useMutation(useMutationInvalidator(simulationService.uploads.validate));

  const validate = useCallback(
    (params: ValidateParams, cb: ValidateCallbacks) => {
      validateLegacy.mutate(params.s3Key, {
        onSuccess: (result) => cb.onSuccess(result as UnifiedSimulationUpload),
        onError: cb.onError,
      });
    },
    [validateLegacy],
  );

  return { validate, isLoading: validateLegacy.isLoading };
}
